import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import BranchService from '../../services/branch/BranchService';
import RestaurantCatalogExtensionService from '../../services/restaurant/RestaurantCatalogExtensionService';
import RestaurantInventoryService from '../../services/restaurant/RestaurantInventoryService';
import { useAuthUser } from '../auth/useAuthUser';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import {
  RestaurantWasteDTO,
  RestaurantWasteFilters,
  RestaurantWasteReason,
} from '../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantInventoryDTO } from '../../types/restaurant/RestaurantInventoryTypes';
import { PageMetadata } from '../../types/restaurant/PagedResponse';

import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';
import { getLocalDateTime } from '../../features/restaurant/waste/restaurantWasteUtils';

export interface RestaurantWasteForm {
  inventoryId: number;
  quantity: string;
  reason: RestaurantWasteReason;
  notes: string;
  occurredAt: string;
}

const initialForm = (): RestaurantWasteForm => ({
  inventoryId: 0,
  quantity: '',
  reason: 'SPOILAGE',
  notes: '',
  occurredAt: getLocalDateTime(),
});

const useRestaurantWaste = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [items, setItems] = useState<RestaurantWasteDTO[]>([]);
  const [inventory, setInventory] = useState<RestaurantInventoryDTO[]>([]);
  const [page, setPage] = useState<PageMetadata | null>(null);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<RestaurantWasteForm>(initialForm);

  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const lastFilters = useRef<RestaurantWasteFilters>({ page: 0, size: 20 });

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((data) => {
        setBranches(data);

        if (data.length) {
          setBranchId(data[0].id);
        }
      })
      .catch(handle)
      .finally(() => setLoadingBranches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickBranch]);

  const load = useCallback(async (filters: RestaurantWasteFilters = lastFilters.current) => {
    if (!branchId) return;

    lastFilters.current = filters;

    setLoading(true);

    try {
      const [waste, stock] = await Promise.all([
        RestaurantCatalogExtensionService.listWaste(branchId, filters),
        RestaurantInventoryService.list(branchId, { activeOnly: true, page: 0, size: 100 }),
      ]);

      setItems(waste.content);
      setPage(waste.page);
      setInventory(stock.content);
    } catch (error) {
      handle(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const selectedInventory = useMemo(
    () => inventory.find((item) => item.id === form.inventoryId),
    [inventory, form.inventoryId]
  );

  const resetForm = () => setForm(initialForm());

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) {
      setModalOpen(false);
    }
  };

  const updateField = <K extends keyof RestaurantWasteForm>(
    field: K,
    value: RestaurantWasteForm[K]
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    if (!branchId || !form.inventoryId || Number(form.quantity) <= 0) return;

    setSaving(true);

    try {
      await RestaurantCatalogExtensionService.createWaste(branchId, {
        ...form,
        quantity: Number(form.quantity),
      });

      setModalOpen(false);
      resetForm();
      await load();
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = () => {
    confirm({
      title: 'Confirmar merma',
      message:
        'Esta operación descontará existencias y no puede editarse posteriormente.',
      icon: 'warning',
      confirmText: 'Registrar y descontar',
      cancelText: 'Volver',
      onConfirm: save,
    });
  };

  return {
    branches,
    branchId,
    items,
    page,
    inventory,
    loadingBranches,
    canPickBranch,
    loading,
    saving,
    modalOpen,
    form,
    selectedInventory,
    setBranchId,
    openCreate,
    closeModal,
    updateField,
    confirmSave,
    load,
  };
};

export default useRestaurantWaste;
